import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supportTicketsApi } from "../api/supportTickets";
import { queryKeys } from "../api/queryKeys";
import {
  invalidateSupportTickets,
  invalidateSupportTicketAndLists,
} from "../api/queryInvalidation";
import { useTenantId } from "./useTenantId";
import type {
  AssignTicketRequest,
  CreateTicketRequest,
  ReplyToTicketRequest,
  SupportAnalyticsFilters,
  SupportAnalyticsTrendFilters,
  SupportTicketDetail,
  SupportTicketFilters,
  UpdateTicketStatusRequest,
} from "../types/supportTicket";

// ================================
// Query Hooks
// ================================

/** My tickets / Company tickets list (ReadOwn | ReadTenant) */
export const useSupportTickets = (
  filters: SupportTicketFilters = {},
  options?: { enabled?: boolean; refetchInterval?: number },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.supportTickets.list(tenantId, filters),
    queryFn: () => supportTicketsApi.getTickets(filters),
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    staleTime: 15000,
    gcTime: 5 * 60 * 1000,
  });
};

/** Triage inbox (ReadAll | ReadAssigned) — defaults to ticketStatus=OPEN server-side */
export const useTriageTickets = (
  filters: SupportTicketFilters = {},
  options?: { enabled?: boolean; refetchInterval?: number },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.supportTickets.triageList(tenantId, filters),
    queryFn: () => supportTicketsApi.getTriageTickets(filters),
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    staleTime: 15000,
    gcTime: 5 * 60 * 1000,
  });
};

/** Ticket detail — marks read + logs VIEW as a side effect on the server */
export const useTicketDetail = (
  id: number | null | undefined,
  options?: { enabled?: boolean; refetchInterval?: number },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.supportTickets.detail(tenantId, id as number),
    queryFn: () => supportTicketsApi.getTicket(id as number),
    enabled: options?.enabled !== false && !!id,
    refetchInterval: options?.refetchInterval,
    staleTime: 10000,
    gcTime: 10 * 60 * 1000,
  });
};

/** Assignee dropdown (Assign) — cached for the triage session */
export const useTicketAssignees = (options?: { enabled?: boolean }) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.supportTickets.assignees(tenantId),
    queryFn: () => supportTicketsApi.getAssignees(),
    enabled: options?.enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/** Analytics summary (SupportAnalytics.Tenant | .All) */
export const useSupportAnalyticsSummary = (
  filters: SupportAnalyticsFilters,
  options?: { enabled?: boolean },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.supportTickets.analyticsSummary(tenantId, filters),
    queryFn: () => supportTicketsApi.getAnalyticsSummary(filters),
    enabled:
      options?.enabled !== false && !!filters.fromDate && !!filters.toDate,
    staleTime: 60000,
    gcTime: 10 * 60 * 1000,
  });
};

/** Analytics trend, for charts */
export const useSupportAnalyticsTrend = (
  filters: SupportAnalyticsTrendFilters,
  options?: { enabled?: boolean },
) => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.supportTickets.analyticsTrend(tenantId, filters),
    queryFn: () => supportTicketsApi.getAnalyticsTrend(filters),
    enabled:
      options?.enabled !== false && !!filters.fromDate && !!filters.toDate,
    staleTime: 60000,
    gcTime: 10 * 60 * 1000,
  });
};

// ================================
// Mutation Hooks
// ================================

/** Submit ticket (SupportTicket.Create) — JSON only; attach files afterward via useUploadTicketAttachment */
export const useCreateTicket = (options?: {
  onSuccess?: (data: SupportTicketDetail) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (ticket: CreateTicketRequest) =>
      supportTicketsApi.createTicket(ticket),
    onSuccess: (data) => {
      invalidateSupportTickets(queryClient, tenantId);
      queryClient.setQueryData(
        queryKeys.supportTickets.detail(tenantId, data.id),
        data,
      );
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/** Reply composer (SupportTicket.Reply / .ReplyInternal) — JSON only; attach files afterward via useUploadTicketAttachment */
export const useReplyToTicket = (options?: {
  onSuccess?: (data: SupportTicketDetail) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({ id, reply }: { id: number; reply: ReplyToTicketRequest }) =>
      supportTicketsApi.replyToTicket(id, reply),
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.supportTickets.detail(tenantId, data.id),
        data,
      );
      invalidateSupportTicketAndLists(queryClient, tenantId, data.id);
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/** Status dropdown + required explanation (SupportTicket.UpdateStatus) */
export const useUpdateTicketStatus = (options?: {
  onSuccess?: (data: SupportTicketDetail) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: UpdateTicketStatusRequest;
    }) => supportTicketsApi.updateTicketStatus(id, request),
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.supportTickets.detail(tenantId, data.id),
        data,
      );
      invalidateSupportTicketAndLists(queryClient, tenantId, data.id);
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/** Assignee picker (SupportTicket.Assign) */
export const useAssignTicket = (options?: {
  onSuccess?: (data: SupportTicketDetail) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: AssignTicketRequest;
    }) => supportTicketsApi.assignTicket(id, request),
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.supportTickets.detail(tenantId, data.id),
        data,
      );
      invalidateSupportTicketAndLists(queryClient, tenantId, data.id);
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
};

/** Soft-delete confirm (SupportTicket.UpdateStatus) */
export const useDeleteTicket = (options?: {
  onSuccess?: (id: number) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (id: number) => supportTicketsApi.deleteTicket(id),
    onSuccess: (result) => {
      queryClient.removeQueries({
        queryKey: queryKeys.supportTickets.detail(tenantId, result.id),
      });
      invalidateSupportTickets(queryClient, tenantId);
      options?.onSuccess?.(result.id);
    },
    onError: options?.onError,
  });
};

/** Resolve a short-lived presigned download URL just before use — do not cache/store it */
export const useDownloadTicketAttachment = () => {
  return useMutation({
    mutationFn: ({
      ticketId,
      attachmentId,
    }: {
      ticketId: number;
      attachmentId: number;
    }) => supportTicketsApi.getAttachmentDownloadUrl(ticketId, attachmentId),
  });
};

/**
 * Presigned attachment upload (init → PUT S3 → complete) for a ticket create
 * or reply. Call once per file after the ticket/reply JSON has been created.
 */
export const useUploadTicketAttachment = (options?: {
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({
      ticketId,
      file,
      messageId,
      onProgress,
    }: {
      ticketId: number;
      file: File;
      messageId?: number;
      onProgress?: (progress: number) => void;
    }) =>
      supportTicketsApi.uploadAttachment(ticketId, file, messageId, onProgress),
    onSuccess: (_attachmentId, { ticketId }) => {
      invalidateSupportTicketAndLists(queryClient, tenantId, ticketId);
    },
    onError: options?.onError,
  });
};
